import { ContentType } from '@jurnee/common/src/constants/ContentTypes';
import { getRandomRequestId } from '../utils/core';
import { Logger, logger, sessionId } from './Logger';

const API_URL = process.env.API_URL;

interface ApiOptions {
  logger: Logger;
}

interface OptionalHeaders {
  'Content-Type': ContentType;
  'X-Filename': string;
}

class Api extends EventTarget {

  private token: string | null = localStorage.getItem('token');

  private logger: Logger;

  constructor(options: ApiOptions) {
    super();
    this.logger = options.logger;
  }

  setToken(token: string) {
    this.token = token;
  }

  getHeaders(requestId: string, optionalHeaders?: OptionalHeaders) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...optionalHeaders
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    headers['X-Request-ID'] = requestId;

    headers['X-Session-ID'] = sessionId;

    return headers;
  }

  private async getData(resp: Response) {
    const contentType = resp.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      try {
        return resp.json();
      } catch (e) {
        return {};
      }
    }

    return resp;
  }

  private async fetch<T = unknown>(url: URL, props: RequestInit, optionalHeaders?: OptionalHeaders): Promise<T> {
    const requestId = getRandomRequestId();
    const headers = this.getHeaders(requestId, optionalHeaders);

    const context = {
      method: props.method,
      path: url.pathname,
      sessionId: sessionId,
      requestId
    };

    this.logger.info(`REQ - ${context.method} ${context.path}`, context);

    const resp = await fetch(url.toString(), { headers, ...props });

    const data = await this.getData(resp);

    if (!resp.ok) {
      this.logger.error(`RES - ${context.method} ${context.path}`, { ...context, status: resp.status });

      if (resp.status === 401) {
        this.dispatchEvent(new Event('unauthorized'));
      }

      throw new Error(data instanceof Response ? await data.text() : data.message);
    }

    this.logger.info(`RES - ${context.method} ${context.path}`, { ...context, status: resp.status });

    return data;
  }

  async post<T = unknown>(path: string, payload = {}): Promise<T> {
    return this.fetch<T>(new URL(path, API_URL), {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async uploadFile<T = unknown>(path: string, file: File): Promise<T> {
    return this.fetch<T>(new URL(path, API_URL), {
      method: 'POST',
      body: file
    }, {
      'Content-Type': file.type as ContentType,
      'X-Filename': encodeURIComponent(file.name)
    });
  }

  async delete<T = unknown>(path: string, payload = {}): Promise<T> {
    return this.fetch<T>(new URL(path, API_URL), {
      method: 'DELETE',
      body: JSON.stringify(payload)
    });
  }

  async put<T = unknown>(path: string, payload = {}): Promise<T> {
    return this.fetch<T>(new URL(path, API_URL), {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async get<T = unknown>(path: string): Promise<T> {
    return this.fetch<T>(new URL(path, API_URL), {
      method: 'GET'
    });
  }

}

export const api = new Api({
  logger
});